import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    Search, 
    Filter, 
    ChevronDown, 
    ChevronLeft,
    ChevronRight,
    Eye, 
    CheckCircle, 
    XCircle,
    Clock,
    Trash2,
    X,
    Mail,
    Phone,
    School,
    User,
    FileText,
    MapPin,
    Calendar,
    Shield,
    Users,
    Briefcase,
    GraduationCap,
    MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getApplications, updateApplicationStatus, deleteApplication } from '@/lib/api';
import { cn } from '@/lib/utils';

const ITEMS_PER_PAGE = 20;

const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'waitlist', label: 'Waitlist' }
];

const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'delegate', label: 'Delegate' },
    { value: 'organization', label: 'Organization' },
    { value: 'pr', label: 'PR' },
    { value: 'academy', label: 'Academy' }
];

const statusColors = {
    pending: 'status-pending',
    approved: 'status-accepted',
    rejected: 'status-rejected',
    waitlist: 'bg-orange-100 text-orange-800'
};

const statusIcons = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle,
    waitlist: Clock
};

const AdminApplications = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [applications, setApplications] = useState([]);
    const [stats, setStats] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [applicationToDelete, setApplicationToDelete] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchApplications = useCallback(async () => {
        setIsLoading(true);
        try {
            const filters = {
                status: statusFilter !== 'all' ? statusFilter : undefined,
                applicationType: typeFilter !== 'all' ? typeFilter : undefined,
                search: searchTerm || undefined,
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                sortBy,
                sortOrder
            };
            const data = await getApplications(filters);
            setApplications(data.applications || []);
            setStats(data.stats);
            setPagination(data.pagination);
        } catch (error) {
            console.error('Error fetching applications:', error);
            setApplications([]);
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter, typeFilter, searchTerm, currentPage, sortBy, sortOrder]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchApplications();
    };

    const handleStatusUpdate = async (id, newStatus) => {
        setIsUpdating(true);
        try {
            await updateApplicationStatus(id, newStatus);
            fetchApplications();
            // Close the modal after successful status update
            setSelectedApplication(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!applicationToDelete) return;
        
        const appId = applicationToDelete._id || applicationToDelete.applicationId;
        try {
            await deleteApplication(appId);
            fetchApplications();
            setApplicationToDelete(null);
            const selectedId = selectedApplication?._id || selectedApplication?.applicationId;
            if (selectedId === appId) {
                setSelectedApplication(null);
            }
        } catch (error) {
            console.error('Error deleting application:', error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground">
                        Applications
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage and review delegate applications
                    </p>
                </div>
                
                {/* Stats Pills */}
                <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full bg-muted text-sm font-medium">
                        Total: {stats?.total || 0}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-warning/10 text-warning text-sm font-medium">
                        Pending: {stats?.pending || 0}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-success/10 text-success text-sm font-medium">
                        Approved: {stats?.approved || 0}
                    </span>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-xl border border-border p-4">
                <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search by name, email, or school..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 input-elegant"
                        />
                    </div>

                    {/* Status Filter */}
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full lg:w-48 input-elegant">
                            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            {statusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Type Filter */}
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-full lg:w-48 input-elegant">
                            <User className="w-4 h-4 mr-2 text-muted-foreground" />
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {typeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Button type="submit" className="btn-navy">
                        Search
                    </Button>
                </form>
            </div>

            {/* Applications Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
                {isLoading ? (
                    <div className="p-8 space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                        ))}
                    </div>
                ) : applications.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-muted/50 border-b border-border">
                                <tr>
                                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                                        Applicant
                                    </th>
                                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                                        School
                                    </th>
                                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                                        Location
                                    </th>
                                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                                        Status
                                    </th>
                                    <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                                        Submitted
                                    </th>
                                    <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {applications.map((app) => {
                                    const StatusIcon = statusIcons[app.status];
                                    const appId = app._id || app.applicationId;
                                    return (
                                        <tr key={appId} className="hover:bg-muted/30 transition-colors" data-testid={`application-row-${appId}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                        <span className="text-primary font-semibold">
                                                            {app.firstName?.charAt(0) || '?'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-foreground">
                                                            {`${app.firstName} ${app.lastName}`}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {app.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-foreground">{app.schoolName}</p>
                                                <p className="text-sm text-muted-foreground">Grade {app.grade}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-foreground">{app.city || '-'}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge className={cn("flex items-center gap-1.5 w-fit", statusColors[app.status])}>
                                                    <StatusIcon className="w-3.5 h-3.5" />
                                                    {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-muted-foreground">
                                                {formatDate(app.createdAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setSelectedApplication(app)}
                                                        className="text-muted-foreground hover:text-foreground"
                                                        data-testid={`view-btn-${appId}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setApplicationToDelete(app)}
                                                        className="text-muted-foreground hover:text-destructive"
                                                        data-testid={`delete-btn-${appId}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                            No applications found
                        </h3>
                        <p className="text-muted-foreground">
                            Try adjusting your filters or search terms
                        </p>
                    </div>
                )}

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                    <div className="border-t border-border p-4 flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)} of {pagination.total} applications
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Previous
                            </Button>
                            <span className="text-sm text-muted-foreground px-3">
                                Page {currentPage} of {pagination.pages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                                disabled={currentPage === pagination.pages}
                            >
                                Next
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Application Detail Modal */}
            <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            Application Details
                        </DialogTitle>
                    </DialogHeader>

                    {selectedApplication && (
                        <div className="space-y-6 py-4">
                            {/* Status Badge & App ID */}
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-3">
                                    <Badge className={cn("text-sm", statusColors[selectedApplication.status])}>
                                        {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground font-mono">
                                        ID: {selectedApplication.applicationId}
                                    </span>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                    {formatDate(selectedApplication.createdAt)}
                                </span>
                            </div>

                            {/* Application Type */}
                            {selectedApplication.applicationType && (
                                <div className="p-3 bg-accent/10 rounded-lg border border-accent/20">
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-accent" />
                                        <span className="font-semibold text-accent">
                                            {selectedApplication.applicationType.charAt(0).toUpperCase() + selectedApplication.applicationType.slice(1)} Başvurusu
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                    <User className="w-4 h-4 text-accent" />
                                    Kişisel Bilgiler
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-start gap-3">
                                        <User className="w-5 h-5 text-accent mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Ad Soyad</p>
                                            <p className="font-medium">{`${selectedApplication.firstName} ${selectedApplication.lastName}`}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Mail className="w-5 h-5 text-accent mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">E-posta</p>
                                            <p className="font-medium break-all">{selectedApplication.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Phone className="w-5 h-5 text-accent mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Telefon</p>
                                            <p className="font-medium">{selectedApplication.phone || 'Belirtilmemiş'}</p>
                                        </div>
                                    </div>
                                    {selectedApplication.dateOfBirth && (
                                        <div className="flex items-start gap-3">
                                            <Calendar className="w-5 h-5 text-accent mt-0.5" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Doğum Tarihi</p>
                                                <p className="font-medium">{new Date(selectedApplication.dateOfBirth).toLocaleDateString('tr-TR')}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Location Information */}
                            {(selectedApplication.city || selectedApplication.district) && (
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-accent" />
                                        Konum Bilgileri
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground">İl</p>
                                            <p className="font-medium">{selectedApplication.city || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">İlçe</p>
                                            <p className="font-medium">{selectedApplication.district || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Education Information */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                    <GraduationCap className="w-4 h-4 text-accent" />
                                    Eğitim Bilgileri
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-start gap-3">
                                        <School className="w-5 h-5 text-accent mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Okul</p>
                                            <p className="font-medium">{selectedApplication.schoolName}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <GraduationCap className="w-5 h-5 text-accent mt-0.5" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Sınıf</p>
                                            <p className="font-medium">{selectedApplication.grade}. Sınıf</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* MUN Experience */}
                            {selectedApplication.experience && (
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-accent" />
                                        MUN Deneyimi
                                    </h3>
                                    <div className="p-3 bg-muted/50 rounded-lg">
                                        <p className="text-sm">{selectedApplication.experience}</p>
                                    </div>
                                </div>
                            )}

                            {/* Committee Preferences */}
                            {selectedApplication.committeePreferences?.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                        <Users className="w-4 h-4 text-accent" />
                                        Komite Tercihleri
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedApplication.committeePreferences.map((committee, i) => (
                                            <Badge key={committee} variant="outline" className="text-xs py-1.5">
                                                {i + 1}. {committee}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Motivation / Why Join */}
                            {selectedApplication.motivation && (
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4 text-accent" />
                                        Motivasyon
                                    </h3>
                                    <div className="p-3 bg-muted/50 rounded-lg">
                                        <p className="text-sm whitespace-pre-wrap">{selectedApplication.motivation}</p>
                                    </div>
                                </div>
                            )}

                            {/* Parent/Guardian Information */}
                            {selectedApplication.parentName && (
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                        <Users className="w-4 h-4 text-accent" />
                                        Veli Bilgileri
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Veli Adı Soyadı</p>
                                            <p className="font-medium">{selectedApplication.parentName}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Veli Telefonu</p>
                                            <p className="font-medium">{selectedApplication.parentPhone}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Health Information */}
                            {selectedApplication.healthInfo && (
                                <div className="space-y-4 p-4 bg-warning/5 rounded-lg border border-warning/20">
                                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-warning" />
                                        Sağlık Bilgileri
                                        <span className="text-xs text-warning font-normal">(Gizli)</span>
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Kronik Hastalık</p>
                                            <p className="font-medium">{selectedApplication.healthInfo.hasChronicIllness ? 'Evet' : 'Hayır'}</p>
                                        </div>
                                        {selectedApplication.healthInfo.allergies && (
                                            <div>
                                                <p className="text-muted-foreground">Alerjiler</p>
                                                <p className="font-medium">{selectedApplication.healthInfo.allergies}</p>
                                            </div>
                                        )}
                                        {selectedApplication.healthInfo.emergencyMedications && (
                                            <div className="col-span-2">
                                                <p className="text-muted-foreground">Acil Durum İlaçları</p>
                                                <p className="font-medium">{selectedApplication.healthInfo.emergencyMedications}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Legal Approvals */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground border-b border-border pb-2 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-accent" />
                                    Yasal Onaylar
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-center gap-2">
                                        {selectedApplication.kvkkConsent ? (
                                            <CheckCircle className="w-4 h-4 text-success" />
                                        ) : (
                                            <XCircle className="w-4 h-4 text-destructive" />
                                        )}
                                        <span className="text-sm">KVKK Onayı</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {selectedApplication.guardianConsent ? (
                                            <CheckCircle className="w-4 h-4 text-success" />
                                        ) : (
                                            <XCircle className="w-4 h-4 text-destructive" />
                                        )}
                                        <span className="text-sm">Veli İzni</span>
                                    </div>
                                </div>
                            </div>

                            {/* Timestamps */}
                            <div className="text-xs text-muted-foreground border-t border-border pt-4">
                                <p>Oluşturulma: {formatDate(selectedApplication.createdAt)}</p>
                                {selectedApplication.updatedAt && selectedApplication.updatedAt !== selectedApplication.createdAt && (
                                    <p>Son Güncelleme: {formatDate(selectedApplication.updatedAt)}</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                                <Button
                                    onClick={() => handleStatusUpdate(selectedApplication._id || selectedApplication.applicationId, 'approved')}
                                    disabled={isUpdating || selectedApplication.status === 'approved'}
                                    className="flex-1 bg-success hover:bg-success/90 text-success-foreground"
                                    data-testid="approve-btn"
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Onayla
                                </Button>
                                <Button
                                    onClick={() => handleStatusUpdate(selectedApplication._id || selectedApplication.applicationId, 'rejected')}
                                    disabled={isUpdating || selectedApplication.status === 'rejected'}
                                    variant="outline"
                                    className="flex-1 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                    data-testid="reject-btn"
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reddet
                                </Button>
                                <Button
                                    onClick={() => handleStatusUpdate(selectedApplication._id || selectedApplication.applicationId, 'pending')}
                                    disabled={isUpdating || selectedApplication.status === 'pending'}
                                    variant="outline"
                                    className="flex-1"
                                    data-testid="pending-btn"
                                >
                                    <Clock className="w-4 h-4 mr-2" />
                                    Beklemede
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!applicationToDelete} onOpenChange={() => setApplicationToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Application</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the application from <strong>{applicationToDelete?.firstName} {applicationToDelete?.lastName}</strong>? 
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminApplications;
